import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { DollarSign, TrendingUp, FileText, CheckCircle, Plus, X, RefreshCw, Clock } from 'lucide-react';
import api from '../api';
import { toast } from 'sonner';

const BillingPage = () => {
  const [billing, setBilling] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    patientId: '',
    patientName: '',
    serviceDate: new Date().toISOString().split('T')[0],
    serviceType: 'Skilled Nursing Visit',
    amount: '',
    insurance: 'Medicare',
    dueDate: ''
  });

  const serviceTypes = [
    'Skilled Nursing Visit',
    'Physical Therapy',
    'Occupational Therapy',
    'Speech Therapy',
    'Home Health Aide',
    'Medical Social Worker',
    'Wound Care',
    'IV Therapy',
    'Assessment Visit'
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [billingRes, patientsRes] = await Promise.all([
        api.get('/billing'),
        api.get('/patients')
      ]);

      if (billingRes.data.success) {
        setBilling(billingRes.data.billing);
      }
      if (patientsRes.data.success) {
        setPatients(patientsRes.data.patients);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddBilling = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/billing', {
        ...formData,
        amount: parseFloat(formData.amount)
      });
      if (response.data.success) {
        toast.success('Billing record created');
        setShowAddModal(false);
        resetForm();
        fetchData();
      }
    } catch (error) {
      console.error('Error creating billing:', error);
      toast.error('Failed to create billing record');
    }
  };

  const handlePatientSelect = (patientId) => {
    const patient = patients.find(p => p.id === patientId);
    if (patient) {
      setFormData({
        ...formData,
        patientId: patient.id,
        patientName: `${patient.firstName} ${patient.lastName}`,
        insurance: patient.insurance
      });
    }
  };

  const resetForm = () => {
    setFormData({
      patientId: '',
      patientName: '',
      serviceDate: new Date().toISOString().split('T')[0],
      serviceType: 'Skilled Nursing Visit',
      amount: '',
      insurance: 'Medicare',
      dueDate: ''
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Paid': return 'bg-green-100 text-green-800';
      case 'Pending': return 'bg-yellow-100 text-yellow-800';
      case 'Submitted': return 'bg-blue-100 text-blue-800';
      case 'Denied': return 'bg-red-100 text-red-800';
      case 'Overdue': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Calculate stats
  const stats = {
    totalRevenue: billing.filter(b => b.status === 'Paid').reduce((sum, b) => sum + b.amount, 0),
    pendingClaims: billing.filter(b => b.status === 'Pending' || b.status === 'Submitted').length,
    pendingValue: billing.filter(b => b.status === 'Pending' || b.status === 'Submitted').reduce((sum, b) => sum + b.amount, 0),
    paidClaims: billing.filter(b => b.status === 'Paid').length,
    collectionRate: billing.length > 0 
      ? Math.round((billing.filter(b => b.status === 'Paid').length / billing.length) * 100)
      : 0
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="billing-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Billing</h1>
            <p className="text-gray-600 mt-1">Manage claims and payments</p>
          </div>
          <div className="flex gap-2">
            <button onClick={fetchData} className="p-2 hover:bg-gray-100 rounded-lg border border-gray-300" title="Refresh">
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button 
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center px-4 py-2 bg-[#ff6634] text-white rounded-lg hover:bg-[#e55a2d] transition-colors"
              data-testid="new-claim-button"
            >
              <Plus className="w-5 h-5 mr-2" />
              New Claim
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid md:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Total Revenue</span>
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
            <p className="text-3xl font-bold text-gray-800">${stats.totalRevenue.toLocaleString()}</p>
            <p className="text-sm text-green-600 mt-1">From paid claims</p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Pending Claims</span>
              <Clock className="w-5 h-5 text-orange-600" />
            </div>
            <p className="text-3xl font-bold text-gray-800">{stats.pendingClaims}</p>
            <p className="text-sm text-gray-600 mt-1">${stats.pendingValue.toLocaleString()} value</p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Paid Claims</span>
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <p className="text-3xl font-bold text-gray-800">{stats.paidClaims}</p>
            <p className="text-sm text-gray-600 mt-1">This period</p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Collection Rate</span>
              <TrendingUp className="w-5 h-5 text-blue-600" />
            </div>
            <p className="text-3xl font-bold text-gray-800">{stats.collectionRate}%</p>
            <p className={`text-sm mt-1 ${stats.collectionRate >= 90 ? 'text-green-600' : 'text-yellow-600'}`}>
              {stats.collectionRate >= 90 ? 'Above target' : 'Below target'}
            </p>
          </div>
        </div>

        {/* Claims Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-800">Claims</h2>
          </div>
          
          {loading ? (
            <div className="p-12 text-center">
              <RefreshCw className="w-8 h-8 animate-spin text-[#ea580c] mx-auto mb-4" />
              <p className="text-gray-500">Loading billing data...</p>
            </div>
          ) : billing.length === 0 ? (
            <div className="p-12 text-center">
              <DollarSign className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No billing records</h3>
              <p className="text-gray-500 mb-4">Create claims to start tracking billing</p>
              <button
                onClick={() => setShowAddModal(true)}
                className="inline-flex items-center px-4 py-2 bg-[#ff6634] text-white rounded-lg hover:bg-[#e55a2d]"
              >
                <Plus className="w-5 h-5 mr-2" />
                New Claim
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Patient</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payer</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {billing.map((claim) => (
                    <tr key={claim.id} className="hover:bg-gray-50" data-testid={`claim-row-${claim.id}`}>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{claim.patientName}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{claim.serviceType}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{claim.insurance}</td>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900">${claim.amount.toFixed(2)}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{new Date(claim.serviceDate).toLocaleDateString()}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{new Date(claim.dueDate).toLocaleDateString()}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(claim.status)}`}>
                          {claim.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Add Billing Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-800">New Billing Claim</h2>
                <button 
                  onClick={() => { setShowAddModal(false); resetForm(); }}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={handleAddBilling} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Patient</label>
                  <select
                    required
                    value={formData.patientId}
                    onChange={(e) => handlePatientSelect(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ea580c] focus:border-transparent"
                  >
                    <option value="">Select a patient</option>
                    {patients.map((patient) => (
                      <option key={patient.id} value={patient.id}>
                        {patient.firstName} {patient.lastName}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Service Type</label>
                  <select
                    value={formData.serviceType}
                    onChange={(e) => setFormData({...formData, serviceType: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ea580c] focus:border-transparent"
                  >
                    {serviceTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Insurance/Payer</label>
                  <select
                    value={formData.insurance}
                    onChange={(e) => setFormData({...formData, insurance: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ea580c] focus:border-transparent"
                  >
                    <option>Medicare</option>
                    <option>Medicaid</option>
                    <option>Private Insurance</option>
                    <option>Blue Cross Blue Shield</option>
                    <option>Aetna</option>
                    <option>United Healthcare</option>
                    <option>CIGNA</option>
                    <option>Private Pay</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount ($)</label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({...formData, amount: e.target.value})}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ea580c] focus:border-transparent"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Service Date</label>
                    <input
                      type="date"
                      required
                      value={formData.serviceDate}
                      onChange={(e) => setFormData({...formData, serviceDate: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ea580c] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                    <input
                      type="date"
                      required
                      value={formData.dueDate}
                      onChange={(e) => setFormData({...formData, dueDate: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ea580c] focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => { setShowAddModal(false); resetForm(); }}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-[#ff6634] text-white rounded-lg hover:bg-[#e55a2d]"
                  >
                    Create Claim
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default BillingPage;
